import { track } from 'src/lib/analytics/utils';
import {
  mapFeatureToDialogOrigin,
  openPopupPromise,
} from 'src/lib/popups/popupOpenClose';
import StateObserver, { now, user } from 'src/StateObserver';
import type { GuestModeOriginFeature } from './guest.analytics.schema';
import * as Promo from 'src/replicant/promo';
import { GCInstant as platform } from 'src/lib/gcinstant';
import { getFooter, isPlatformGuest, withLoading } from 'src/lib/utils';
import { hasWebPlayerCredentials } from '@play-co/replicant';
import { config } from 'src/replicant/config';
import {
  getWebPlayerCredentials,
  removeWebPlayerCredentials,
  WebPlayerCredentials,
} from '@play-co/replicant/lib/core/client/WebPlayableReplicantFactory';
import {
  getStateSummary,
  isGuestUser,
  StateSummary,
} from 'src/replicant/guest/guest.getters';
import {
  conflictResolutionMinLevel,
  conflictResolutionMinTimestamp,
} from 'src/replicant/guest/guest.ruleset';
import { Feature } from 'src/replicant/analytics';
import {
  tryLocalStorage,
  tryToSetItemToLocalStorage,
} from 'src/lib/tryLocalStorage';
import { State } from 'src/replicant/State';
import { captureGenericError } from 'src/lib/sentry';
import {
  canDisplayNativeLogin,
  loginToLINE,
} from 'src/lib/native/native.login';
import { getMatch2Level } from 'src/replicant/match2/match2.getters';
import { isPromoReady } from 'src/replicant/promo/promo.getters';

/**
 * Prompts guest users to log in with LINE using the new popup flow.
 *
 * displays the LINE login popup, tracks interactions, resets the native device token,
 * and triggers the LINE login sequence (which reloads the app afterward).
 *
 * @param feature - The origin feature triggering the guest login prompt.
 * @returns A promise that resolves when the login sequence is complete or if the flow is cancelled.
 */
export async function promptLINELoginNew(feature: GuestModeOriginFeature) {
  // Only for guest users
  if (!isPlatformGuest(platform) || !isGuestUser(user())) {
    return;
  }

  // Show new line login popup
  track('LoginWithLineShow', { feature });
  const login = await openPopupPromise('popupNativeLineGuestLoginNew', {
    origin: mapFeatureToDialogOrigin(feature),
  });

  // Remember that promo was shown, if ready
  if (isPromoReady(user(), 'guestModeLogin', now())) {
    await StateObserver.invoke.promoWasShown('guestModeLogin');
  }

  // User cancelled the login
  if (!login) {
    track('LoginWithLineCancel', { feature });
    return;
  }

  // User confirmed the login
  track('LoginWithLineConfirm', { feature });

  // Reset PN token to stop sending PNs for the current user.
  // This will be set again if the user logs in again with the same user.
  const os = platform.legacyNative?.platform.os;
  if (!os) return;
  await StateObserver.invoke.setNativeDeviceToken({
    [os]: undefined,
  });

  // Run the login sequence and reload the app
  await loginToLINE();
}

/**
 * Determines whether the new LINE login prompt should be shown to the user on app launch.
 *
 * If the user has played more than 50 levels, the prompt is shown once per day on login.
 *
 * @param state - The current game state.
 * @param now - The current timestamp.
 * @returns `true` if the prompt should be shown, otherwise `false`.
 */
export function shouldShowNewPromptLINELoginOnLaunch(
  state: State,
  now: number,
): boolean {
  // Only for guest users
  if (!isPlatformGuest(platform) || !isGuestUser(state)) {
    return false;
  }

  // If the user has played more than 50 levels, show popup every login once per day
  if (getMatch2Level(state) > 50) {
    return Promo.getters.isPromoReady(state, 'guestModeLogin', now);
  }

  // Default to false
  return false;
}

/**
 * Consume guest mode local state to migrate to the new online guest mode.
 */
export function consumeGuestModeLocalState() {
  // Retrieve guest mode id from local storage.
  let guestModeId =
    tryLocalStorage()?.getItem(`${process.env.APP_ID}:playerID`) ?? undefined;
  if (!guestModeId) return undefined;

  // Retrieve replicant's offline state from local storage.
  const stateKey = `Replicantdb_buruburu_default_state_${guestModeId}`;
  const offlineState = tryLocalStorage()?.getItem(stateKey) ?? undefined;

  // Try to keep a local backup of the offline state just in case.
  if (offlineState) {
    tryToSetItemToLocalStorage(stateKey + '_bak', offlineState);
  }

  // Instantly remove the offline state from local storage.
  // This is to prevent sending it on every login.
  tryLocalStorage()?.removeItem(stateKey);

  return offlineState;
}

/**
 * Handles the migration of user state when transitioning from guest mode to a logged-in state.
 *
 * This function checks for the presence of guest mode credentials in local storage and attempts to
 * resolve any conflicts between the guest mode state and the current logged-in state. If a conflict
 * is detected (e.g., both states have progress above a certain threshold), the user is prompted to
 * select which state to keep. The selected state is then applied, and the credentials are updated accordingly.
 *
 * @param state - The current application state.
 * @param storage - The local storage instance to retrieve credentials and state.
 * @param now - The current timestamp in milliseconds.
 * @returns A promise that resolves to the selected or resolved `StateSummary`, or `undefined` if no migration was performed.
 */
export async function handleGuestModeStateMigration(
  state: State,
  storage: Storage | null,
  now: number,
): Promise<StateSummary | undefined> {
  // If local storage is not available, skip the migration.
  if (!storage) {
    captureGenericError('Local storage is not available', null);
    console.warn(
      'Local storage is not available, skipping guest mode migration',
    );
    return undefined;
  }

  // Only run outside of guest mode and if the user has played in guest mode.
  if (!hasWebPlayerCredentials(config.appName, storage)) {
    return undefined;
  }

  // Get credentials from local storage.
  const credentials = getWebPlayerCredentials(config.appName, storage);

  // No credentials, bail.
  if (!credentials) return undefined;

  // Fetch the guest mode state and the current LINE state
  const lineState = getStateSummary(state, 'line', state.lastLoginAt ?? now);
  const guestModeState =
    await StateObserver.replicant.asyncGetters.getStateSummary(credentials.id);

  // Check if the guest mode state is still valid based on last played timestamp.
  if (guestModeState.lastPlayedAt < conflictResolutionMinTimestamp) {
    // Guest state is too old, and should be ignored. Track the auto resolution event.
    track('LoginConflictAutoResolve', {
      choice: 'keep_line_because_outdated_guest',
      guestLevel: guestModeState?.matchLevel ?? 0,
      lineLevel: lineState?.matchLevel ?? 0,
      guestGems: guestModeState?.gems ?? 0,
      lineGems: lineState?.gems ?? 0,
      guestLastLoginMS: guestModeState?.lastPlayedAt ?? 0,
      lineLastLoginMS: lineState?.lastPlayedAt ?? 0,
      guestCredentialsID: credentials.id,
      guestPaidGems: guestModeState?.paidGems ?? 0,
      linePaidGems: lineState?.paidGems ?? 0,
    });
    // Remove the guest mode credentials from local storage.
    removeWebPlayerCredentials(config.appName, storage);
    return undefined;
  }

  // Create available states for conflict resolution.
  const states = [
    // Current line state.
    lineState,
    // Guest mode state.
    guestModeState,
  ]
    // Sort by match level in descending order.
    .sort((a, b) => b.matchLevel - a.matchLevel)
    // Filter users who did pass tutorial
    .filter((s) => s.matchLevel >= conflictResolutionMinLevel);

  // No relevant state continue with line state
  if (states.length === 0) {
    // Track the auto resolution event
    track('LoginConflictAutoResolve', {
      choice: 'keep_line',
      guestLevel: guestModeState?.matchLevel ?? 0,
      lineLevel: lineState?.matchLevel ?? 0,
      guestGems: guestModeState?.gems ?? 0,
      lineGems: lineState?.gems ?? 0,
      guestLastLoginMS: guestModeState?.lastPlayedAt ?? 0,
      lineLastLoginMS: lineState?.lastPlayedAt ?? 0,
      guestCredentialsID: credentials.id,
      guestPaidGems: guestModeState?.paidGems ?? 0,
      linePaidGems: lineState?.paidGems ?? 0,
    });

    // No relevant states, just remove the guest mode credentials.
    removeWebPlayerCredentials(config.appName, storage);
    return undefined;
  }

  // If there's only one relevant state, fast forward to the only available state.
  // If there are no relevant states, fast forward to the highest match level state - This can happen if both states are below the conflict resolution level.
  if (states.length === 1) {
    const selectedState = states[0];

    // Track the auto resolution event
    track('LoginConflictAutoResolve', {
      choice: `keep_${selectedState.origin}`,
      guestLevel: guestModeState?.matchLevel ?? 0,
      lineLevel: lineState?.matchLevel ?? 0,
      guestGems: guestModeState?.gems ?? 0,
      lineGems: lineState?.gems ?? 0,
      guestLastLoginMS: guestModeState?.lastPlayedAt ?? 0,
      lineLastLoginMS: lineState?.lastPlayedAt ?? 0,
      guestCredentialsID: credentials.id,
      guestPaidGems: guestModeState?.paidGems ?? 0,
      linePaidGems: lineState?.paidGems ?? 0,
    });

    // Resolve the conflict by applying auto selected state
    return await resolveConflict(credentials, storage, selectedState);
  }

  // Dismiss the splash so the popup is not blocked.
  await platform.dismissLoadingScreenAsync({
    launchPath: 'guest_login_conflict',
  });

  // Track the event of showing the conflict resolution dialog.
  const guestSave = states.find((s) => s.origin === 'guest');
  const lineSave = states.find((s) => s.origin === 'line');

  // Track analytics for the conflict resolution dialog
  track('LoginConflictDialogShown', {
    guestLevel: guestSave?.matchLevel ?? 0,
    lineLevel: lineSave?.matchLevel ?? 0,
    guestGems: guestSave?.gems ?? 0,
    lineGems: lineSave?.gems ?? 0,
    guestLastLoginMS: guestSave?.lastPlayedAt ?? 0,
    lineLastLoginMS: lineSave?.lastPlayedAt ?? 0,
  });

  // Show the popup to select which state to keep.
  const selectedState = await openPopupPromise('popupSelectSave', {
    states,
  });

  // If the user didn't select a state, show an error and return.
  // Should not happen, but just in case.
  if (selectedState === undefined) {
    track('LoginConflictError', {
      error: 'State was not selected',
    });

    captureGenericError('State was not selected', null);

    await openPopupPromise('popupError', {
      message: 'State was not selected',
    });

    return undefined; // exit
  }

  // Show confirmation popup if the selected state has a lower match level than the LINE state.
  if (lineSave && selectedState.matchLevel < lineSave.matchLevel) {
    const result = await openPopupPromise('popupSelectSaveConfirmation', {
      state: selectedState,
    });

    // User cancelled the confirmation, exit prematurely.
    // Re-attempt of a state selection will happen again on next launch.
    if (!result) return undefined;
  }

  // Track user selection.
  track('LoginConflictConfirm', {
    choice: `keep_${selectedState.origin}`,
    chosenLevel: selectedState.matchLevel,
  });

  // Resolve the conflict by applying the selected state.
  return await resolveConflict(credentials, storage, selectedState);
}

/**
 * Resolve the conflict between the guest mode state and the LINE state by applying the selected state.
 * @param credentials - The credentials of the guest mode state.
 * @param selectedSummary - The selected state summary to apply.
 * @returns {Promise<StateSummary | undefined>} A promise that resolves to the selected state summary.
 */
async function resolveConflict(
  credentials: WebPlayerCredentials,
  storage: Storage,
  selectedSummary: StateSummary,
): Promise<StateSummary | undefined> {
  // If the selected state is from LINE, we don't need to do anything just delete the guest mode reference.
  if (selectedSummary.origin === 'line') {
    removeWebPlayerCredentials(config.appName, storage);
    return selectedSummary;
  }

  // Otherwise, we need to restore from the guest state.
  const request = await withLoading(
    () =>
      StateObserver.invoke.asyncRestoreFromGuest({
        credentials,
      }),
    {
      feature: Feature.NATIVE._,
      subFeature: Feature.NATIVE.GUEST_SELECT_SAVE,
    },
  );

  // Make sure the minigame button is updated.
  void getFooter()?.minigameButton.playLevelUp();

  // Remove the guest mode credentials from local storage if the user successfully restored.
  if (request.success) {
    removeWebPlayerCredentials(config.appName, storage);
    return selectedSummary;
  }

  // If the request failed, show an error popup and log the error
  track('LoginConflictError', {
    error: 'Failed to resolve state conflict',
  });

  // Report the error to Sentry
  captureGenericError('Failed to resolve state conflict', request.error, {
    data: selectedSummary,
  });

  // Show an error popup with the error message.
  await openPopupPromise('popupError', {
    message: request.error ?? 'Unknown error',
  });

  return undefined; // Return undefined to indicate failure
}

/**
 * Check if user can migrate from guest mode - might not actually migrate if no local state found
 */
export function canMigrateFromGuestMode() {
  return (
    canDisplayNativeLogin() &&
    hasWebPlayerCredentials(config.appName, tryLocalStorage() || undefined) &&
    platform.platformID === 'web'
  );
}

/**
 * Store if user has migrated their state from guest mode
 */
let guestMigration = false;

/**
 * Set if user has migrated their state from guest mode
 */
export function setGuestMigration(value: boolean) {
  guestMigration = value;
}

/**
 * Check if user has migrated their state from guest mode
 */
export function isGuestMigration() {
  return guestMigration;
}
